import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import cloudStorageService from '../../services/cloudStorageService';
import StorageOverviewCard from './components/StorageOverviewCard';
import StorageConfigPanel from './components/StorageConfigPanel';
import EncryptionManagementPanel from './components/EncryptionManagementPanel';
import AccessControlPanel from './components/AccessControlPanel';
import AuditMonitoringPanel from './components/AuditMonitoringPanel';
import BackupRecoveryPanel from './components/BackupRecoveryPanel';

const SecureCloudStorageManagementCenter = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [storageMetrics, setStorageMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const metrics = await cloudStorageService?.getStorageMetrics();
      setStorageMetrics(metrics);
      setError('');
    } catch (err) {
      console.error('Error loading storage data:', err);
      setError(err?.message || 'Failed to load storage data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const tabs = [
    { id: 'overview', label: 'Storage Overview', icon: 'HardDrive' },
    { id: 'encryption', label: 'Encryption Management', icon: 'Lock' },
    { id: 'access', label: 'Access Control', icon: 'Shield' },
    { id: 'audit', label: 'Audit Monitoring', icon: 'FileSearch' },
    { id: 'backup', label: 'Backup & Recovery', icon: 'Database' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <Header />
      
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Icon name="Cloud" size={28} className="text-blue-600" />
                Secure Cloud Storage Management Center
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Azure Blob Storage & AWS S3 infrastructure with PNG labor law compliance
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                onClick={handleRefresh}
                loading={refreshing}
              >
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Storage Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StorageOverviewCard
            title="Total Storage"
            value={storageMetrics?.totalStorageGB ? `${storageMetrics?.totalStorageGB?.toFixed(2)} GB` : 'Loading...'}
            icon="HardDrive"
            color="blue"
            description="Across all clients"
          />
          <StorageOverviewCard
            title="Encryption Status"
            value={storageMetrics?.encryptionActive ? 'Active' : 'Inactive'}
            icon="Lock"
            color="green"
            description="AES-256 encryption"
          />
          <StorageOverviewCard
            title="Access Control"
            value={storageMetrics?.accessControlHealth || 'Healthy'}
            icon="Shield"
            color="amber"
            description="RBAC enabled"
          />
          <StorageOverviewCard
            title="Backup Status"
            value={storageMetrics?.lastBackup ? 'Completed' : 'Pending'}
            icon="Database"
            color="green"
            description={storageMetrics?.lastBackup || 'No recent backup'}
          />
        </div>

        {/* Main Content */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-slate-700">
            <div className="flex overflow-x-auto">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400' :'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:border-gray-300'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <Icon name="Loader" size={32} className="animate-spin text-blue-600 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400">Loading storage data...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <Icon name="AlertCircle" size={32} className="text-red-600 mx-auto mb-3" />
                <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
                <Button variant="outline" size="sm" onClick={loadData}>
                  Try Again
                </Button>
              </div>
            ) : (
              <>
                {activeTab === 'overview' && <StorageConfigPanel metrics={storageMetrics} onRefresh={loadData} />}
                {activeTab === 'encryption' && <EncryptionManagementPanel onRefresh={loadData} />}
                {activeTab === 'access' && <AccessControlPanel onRefresh={loadData} />}
                {activeTab === 'audit' && <AuditMonitoringPanel />}
                {activeTab === 'backup' && <BackupRecoveryPanel metrics={storageMetrics} onRefresh={loadData} />}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecureCloudStorageManagementCenter;